import { z } from 'zod'
import type { AgentsPayloads } from './agents.ts'
import type { ArtifactsPayloads } from './artifacts.ts'
import type { AuthPayloads } from './auth.ts'
import type { FoldersPayloads } from './folders.ts'
import type { GroupsPayloads } from './groups.ts'
import type { KnowledgePayloads } from './knowledge.ts'
import type { MarketplacePayloads } from './marketplace.ts'
import type { NotificationsPayloads } from './notifications.ts'
import type { PeoplePayloads } from './people.ts'
import type { RunsPayloads } from './runs.ts'
import type { SharingPayloads } from './sharing.ts'
import type { SystemsPayloads } from './systems.ts'

/**
 * The protocol half of PRD-27's server seam: which sentences the server can ask a client to
 * render, and what each one needs in order to be rendered.
 *
 * ── Why a key and not the code ──
 *
 * `ApiIssue.code` is the machine failure class. It drives retry-safety and the HTTP status,
 * and it is deliberately coarse: `NotFoundError` is thrown 123 times across this API and all
 * 123 carry the code `not_found`. Asking that code to also identify a SENTENCE collapses 123
 * distinct refusals into one generic "Not found." — which is how a localization loses exactly
 * the information a person needed (F-1, F-27, D-4).
 *
 * So the code stays what it is, and a second, finer identifier carries the sentence.
 *
 * ── Why the payloads are declared here and the words are not ──
 *
 * This package is the CONTRACT. It says that `knowledge.base_at_document_limit` exists and
 * that rendering it needs a `base` and a `limit`. It does not say what the sentence is, in any
 * language — that lives with all the other copy, in `apps/web/src/i18n/messages/<locale>/`,
 * because a sentence is copy and belongs where a translator can find it (D-5, amended).
 *
 * What this buys: the web catalogue is typed `Record<MessageKey, …>`, so adding a key here
 * without writing its sentence is a COMPILE error across the workspace boundary, and writing
 * a sentence that interpolates a parameter the server never sends is one too.
 *
 * ── Why the wire field is a plain string ──
 *
 * `ApiIssue.messageKey` is `z.string()`, not an enum of these keys. A server that has learned
 * a new key must not break a client that has not: the client falls back to the English
 * `message`, which is on the wire for exactly this reason and is never removed. Strictness
 * belongs at the two ends — typed at the throw site, exhaustive in the catalogue — not in the
 * middle where it would turn a deploy-order problem into an outage.
 */

/** No parameters. Written out rather than `{}`, which TypeScript treats as "any non-nullish". */
export type NoParams = Record<never, never>

/**
 * Every renderable sentence, with the parameters it interpolates.
 *
 * Keys are `<domain>.<what_happened>`, snake_case after the dot, and they are stable: a key is
 * an identifier a stored notification row may still be holding months later, so it is added
 * and deprecated, never renamed in place.
 */
export interface MessagePayloads
  extends AuthPayloads,
    RunsPayloads,
    ArtifactsPayloads,
    KnowledgePayloads,
    FoldersPayloads,
    AgentsPayloads,
    SharingPayloads,
    PeoplePayloads,
    GroupsPayloads,
    SystemsPayloads,
    MarketplacePayloads,
    NotificationsPayloads {
  // --- generic failures the client itself can produce ------------------------
  'common.request_failed': { status: number }
  'common.session_expired': NoParams
  'common.connection_lost': NoParams
  'common.unexpected': NoParams
}

export type MessageKey = keyof MessagePayloads

/**
 * The wire shape of a message's parameters, declared ONCE.
 *
 * Deliberately narrow: a parameter is a value to interpolate — never a pre-built phrase (D-20)
 * and never structured data a client would have to know how to read.
 *
 * A list of strings is admitted, and it is the one compound value that is: a list has to cross
 * the wire AS a list (D-19, LOC-016), because the conjunction belongs to the reader's language.
 * A server that joins names with ', ' has decided the grammar of a language it does not know
 * it is writing for — English wants "Legal, Sales, and Finance", Portuguese "Legal, Sales e
 * Finance", and neither is a comma list. The first cut of this type had no room for one, which
 * is exactly how three call sites came to pre-join.
 *
 * It was also copy-pasted into eight schemas across five files before it was named. One
 * definition now, so widening it again is one line rather than a hunt.
 */
export const MessageParamsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])
)
export type MessageParams = z.infer<typeof MessageParamsSchema>

/** A key with its parameters, as the server hands it over. */
export type LocalizableMessage<K extends MessageKey = MessageKey> = {
  key: K
  params: MessagePayloads[K]
}

/** Narrowing helper for the throw sites: keeps the key and its payload checked together. */
export const message = <K extends MessageKey>(key: K, params: MessagePayloads[K]): LocalizableMessage<K> => ({
  key,
  params
})
